import { useCallback, useEffect, useRef, useState } from 'react';
import RecordRTC, { Options, StereoAudioRecorder } from 'recordrtc';

type RecorderStatus = 'idle' | 'recording' | 'no-media-devices' | 'access-denied' | 'no-microphone' | 'error';

type AudioRecorder = {
  status: RecorderStatus;
  start: () => Promise<boolean>;
  stop: () => Promise<string>;
};

export function useAudioRecorder(errorHandler: (error: unknown) => void): AudioRecorder {
  const streamRef = useRef<MediaStream>();
  const audioRecorderRef = useRef<RecordRTC>();

  const [recorderStatus, setRecorderStatus] = useState<RecorderStatus>(
    window.navigator.mediaDevices ? 'idle' : 'no-media-devices',
  );

  const initialize = useCallback(async (): Promise<boolean> => {
    try {
      const mediaStream = await window.navigator.mediaDevices.getUserMedia({ audio: true, video: false });

      streamRef.current = mediaStream;

      const options: Options = {
        type: 'audio',
        recorderType: StereoAudioRecorder,
        numberOfAudioChannels: 2,
      };

      audioRecorderRef.current = new RecordRTC(mediaStream, options);

      return true;
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') setRecorderStatus('access-denied');
        if (error.name === 'NotFoundError') setRecorderStatus('no-microphone');
      } else {
        setRecorderStatus('error');
        errorHandler(error);
      }

      return false;
    }
  }, [errorHandler]);

  const start = useCallback(async (): Promise<boolean> => {
    const initialized = await initialize();
    if (!initialized) return false;

    const { current: audioRecorder } = audioRecorderRef;
    if (!audioRecorder) return false;

    setRecorderStatus('recording');

    audioRecorder.reset();
    audioRecorder.startRecording();

    return true;
  }, [initialize]);

  const clearRecordingIndicator = useCallback(() => {
    const { current: stream } = streamRef;
    if (stream)
      stream.getTracks().forEach(track => {
        track.stop();
      });
  }, []);

  const stop = useCallback(async (): Promise<string> => {
    const { current: audioRecorder } = audioRecorderRef;
    if (!audioRecorder) throw new Error('No audioRecorder started.');

    await new Promise<void>(resolve => audioRecorder.stopRecording(resolve));

    clearRecordingIndicator();

    const blob = audioRecorder.getBlob();

    setRecorderStatus('idle');
    return window.URL.createObjectURL(blob);
  }, [clearRecordingIndicator]);

  const audioRecorder: AudioRecorder = {
    status: recorderStatus,
    start,
    stop,
  };

  useEffect(() => {
    return (): void => clearRecordingIndicator();
  }, [clearRecordingIndicator]);

  return audioRecorder;
}
